import * as React from "react";

import {
  Grid,
  Paper,
  TextField,
  Typography,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Skeleton,
  IconButton,
  MoreVertical,
  MenuItem,
  Button,
} from "mpulse-design-system";
import InfiniteScroll from "react-infinite-scroll-component";
import { alpha, Box, Menu, styled } from "@mui/material";

import MetadataService from "../../services/Metadata/MetadataService";
import { pluralize } from "../../utils/utils";

import MetadataStatusChip from "../MetadataStatusChip/MetadataStatusChip";
import AddMetadataButton from "../AddMetadataButton/AddMetadataButton";
import StatusFilterBox from "../StatusFilterBox/StatusFilterBox";
import MetadataPopup from "../MetadataPopup/MetadataPopup";
import ArchiveConfirmationDialog from "./ArchiveConfirmationDialog";
import RestoreConfirmationDialog from "./RestoreConfirmationDialog";

const MetadataTable = ({ index, value, account }) => {
  const [popupProps, setPopupProps] = React.useState({ hidden: true });
  const [hasNext, setHasNext] = React.useState({ data: false, filter: false });
  const [nextSet, setNextSet] = React.useState({ data: "", filter: "" });
  const [total, setTotal] = React.useState({ data: 0, filter: 0 });
  const [data, setData] = React.useState([]);
  const [filter, setFilter] = React.useState([]);
  const [fetchingFilter, setFetchingFilter] = React.useState(false);
  const [filterActive, setFilterActive] = React.useState(false);
  const [filterSearchText, setFilterSearchText] = React.useState("");
  const [filterStatus, setFilterStatus] = React.useState("");
  const [searchTimer, setSearchTimer] = React.useState(0);

  const accountId = account.id;
  const { metadataLabel } = account;

  const columnsMap = {
    requested_publish_date: {
      title: "Publish Date",
      size: "12%",
      nested: false,
    },
    brand: { title: "Brand", size: "22%", nested: true },
    client_department_id: { title: "Dept. ID", size: "12%", nested: false },
    friendly_name: {
      title: "Dept. Name/Dept. Friendly Name",
      size: "23%",
      nested: true,
    },
    status: { title: "Status", size: "15%", nested: false },
  };
  const columns = Object.keys(columnsMap);

  const resetFilterState = (fullReset = true, value = "") => {
    setTotal({ ...total, filter: 0 });
    setHasNext({ ...hasNext, filter: false });
    setNextSet({ ...nextSet, filter: "" });
    setFilter([]);
    if (fullReset) {
      setFilterActive(false);
      setFilterSearchText(value);
      setFilterStatus("");
    }
  };

  const fetchData = () => {
    const key = filterActive ? "filter" : "data",
      setDataOrFilter = filterActive ? setFilter : setData;
    setTimeout(() => {
      MetadataService.getMetadataShallow(accountId, nextSet[key]).then(
        ({ count, next, results }) => {
          if (total !== count) setTotal({ ...total, [key]: count });
          setHasNext({ ...hasNext, [key]: Boolean(next) });
          setNextSet({ ...nextSet, [key]: next ? next : "" });
          setDataOrFilter([...data, ...results]);
        }
      );
    }, 500); // Small delay to prevent lag from loading next set
  };

  const fetchInitialFilter = ({
    status = "",
    searchText = "",
    includeSearchText = true,
  }) => {
    if (!status) status = filterStatus;
    if (!searchText && includeSearchText) searchText = filterSearchText;

    const isClientDepartmentId = !isNaN(parseInt(searchText));
    MetadataService.getMetadataShallow(
      accountId,
      "",
      isClientDepartmentId ? "client_department_id" : "friendly_name",
      searchText,
      status
    ).then(({ count, next, results }) => {
      if (count > 0) {
        setTotal({ ...total, filter: count });
        setHasNext({ ...hasNext, filter: Boolean(next) });
        setNextSet({ ...nextSet, filter: next ? next : "" });
        setFilter(results);
      }
      setFetchingFilter(false);
    });
  };

  const handleSearch = (e) => {
    clearTimeout(searchTimer);
    const value = e.target.value;
    setFilterSearchText(value);
    if (value && value.length >= 3) {
      resetFilterState(false);
      setFetchingFilter(true);
      setFilterActive(true);
      setSearchTimer(
        setTimeout(() => {
          // TODO: FRIENDLY_NAME IS HARD CODED NEED TO MAKE THIS CONFIGURABLE
          fetchInitialFilter({ searchText: value });
        }, 2000)
      ); // WAIT 2 seconds before submitting to make sure theres no more input
    } else if (filterActive && filterStatus !== "") {
      fetchInitialFilter({ status: filterStatus, includeSearchText: false });
    } else {
      resetFilterState(true, value);
    }
  };

  const refreshTable = () => {
    resetFilterState(false);
    setFetchingFilter(true);
    setFilterActive(true);
    fetchInitialFilter({});
  };

  const prependToData = (newMetadata, index = -1) => {
    if (index !== -1) {
      let newData = data;
      if (filterActive) {
        let newFilterData = filter;
        newFilterData[index] = newMetadata;
        setFilter(newFilterData);

        newData = data.map((item) => {
          if (item.id === newMetadata.id) {
            return newMetadata;
          }
          return item;
        });
        setData(newData);
      } else {
        newData[index] = newMetadata;
        setData(newData);
      }
    } else {
      setData([...[newMetadata], ...data]);
    }
  };

  React.useEffect(() => {
    fetchData();

    return () => {
      setTotal({ data: 0, filter: 0 });
      setHasNext({ data: false, filter: false });
      setNextSet({ data: "", filter: "" });
      setData([]);
      setFilter([]);
      setFilterActive(false);
      setFetchingFilter(false);
      setFilterStatus("");
      setFilterSearchText("");
    };
  }, []);

  return (
    <Box hidden={value !== index}>
      <Paper
        variant="elevation"
        elevation={0}
        square
        sx={{
          padding: (theme) => `${theme.spacing(2)} ${theme.spacing(3)}`,
          backgroundColor: (theme) => theme.palette.common.white,
        }}
      >
        <Grid container spacing={2} alignItems="center">
          <Grid item xxs={4}>
            <TextField
              placeholder={`Search ${pluralize(metadataLabel, {
                pluralizeAnyway: true,
                returnCount: false,
              })}...`}
              onChange={handleSearch}
              value={filterSearchText}
            />
          </Grid>
          <Grid item xs="auto">
            <StatusFilterBox
              status={filterStatus}
              setStatus={setFilterStatus}
              fetchFilter={fetchInitialFilter}
              {...{ setFetchingFilter, setFilterActive, resetFilterState }}
            />
          </Grid>
          {filterActive && !fetchingFilter ? (
            <Grid item xs="auto">
              <Button
                onClick={resetFilterState}
                color="secondary"
                sx={{ width: 78, height: 40 }}
              >
                Clear
              </Button>
            </Grid>
          ) : null}
          <Grid item xs>
            <Typography color="textTertiary">
              Showing{" "}
              {pluralize("item", {
                count: filterActive ? total.filter : total.data,
              })}
            </Typography>
          </Grid>
          <Grid item container xs justifyContent="flex-end">
            <AddMetadataButton
              metadataLabel={metadataLabel}
              setPopupProps={setPopupProps}
              sx={{ justifySelf: "right" }}
            />
          </Grid>
        </Grid>
      </Paper>
      <InfiniteScroll
        next={fetchData}
        hasMore={filterActive ? hasNext.filter : hasNext.data}
        loader={<TableRowSkeleton colSpan={columns.length + 1} />}
        dataLength={filterActive ? filter.length : data.length}
      >
        <TableContainer
          sx={{ borderRadius: "unset !important", boxSizing: "border-box" }}
        >
          <Table>
            <TableHead>
              <TableRow>
                {columns.map((column, i) => {
                  return (
                    <StyledTableCell
                      key={i}
                      sx={{ width: columnsMap[column].size }}
                    >
                      {columnsMap[column].title}
                    </StyledTableCell>
                  );
                })}
                <StyledTableCell key={"actions"} sx={{ width: "8%" }}>
                  Actions
                </StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.length >= 1 && !fetchingFilter && !filterActive ? (
                data.map((item, i) => {
                  return (
                    <TableDataItem
                      key={`table-data-item-${i}`}
                      {...{
                        accountId,
                        columns,
                        columnsMap,
                        item,
                        i,
                        metadataLabel,
                        setPopupProps,
                        refreshTable,
                      }}
                    />
                  );
                })
              ) : !fetchingFilter && filter.length >= 1 ? (
                filter.map((item, i) => {
                  return (
                    <TableDataItem
                      key={`table-data-item-${i}`}
                      {...{
                        accountId,
                        columns,
                        columnsMap,
                        item,
                        i,
                        metadataLabel,
                        setPopupProps,
                        refreshTable,
                      }}
                    />
                  );
                })
              ) : !fetchingFilter && filterActive ? (
                <TableRow>
                  <StyledTableCell colSpan={columns.length + 1}>
                    No Results.
                  </StyledTableCell>
                </TableRow>
              ) : (
                <TableRow>
                  <StyledTableCell colSpan={columns.length + 1}>
                    <Skeleton />
                  </StyledTableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </InfiniteScroll>
      {!popupProps.hidden ? (
        <MetadataPopup
          {...{ ...popupProps, setPopupProps, account, prependToData }}
        />
      ) : null}
    </Box>
  );
};

const TableDataItem = ({
  accountId,
  columns,
  columnsMap,
  item,
  i,
  metadataLabel,
  setPopupProps,
  refreshTable,
}) => {
  const [anchorEl, setAnchorEl] = React.useState();
  const [confirmArchiveOpen, setConfirmArchiveOpen] = React.useState(false);
  const [confirmRestoreOpen, setConfirmRestoreOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const openMenu = (e) => {
    setAnchorEl(e.currentTarget);
  };

  const closeMenu = () => {
    setAnchorEl(null);
  };

  const handleView = () => {
    MetadataService.getMetadata(accountId, item.client_department_id).then(
      (metadata) => {
        setPopupProps({
          index: i,
          hidden: false,
          isViewMode: true,
          metadata,
          handleEdit,
        });
      }
    );
  };

  const handlePromptArchive = async (confirm) => {
    if (confirm) {
      setLoading(true);
      await handleArchive();
      setLoading(false);
    }
    setConfirmArchiveOpen(false);
  };

  const handlePromptRestore = async (confirm) => {
    if (confirm) {
      setLoading(true);
      await handleRestore();
      setLoadingFalse();
    }
    setConfirmRestoreOpen(false);
  };

  const handleArchive = async () => {
    await MetadataService.archive(item);
    refreshTable();
  };

  const handleRestore = async () => {
    await MetadataService.restore(item);
    refreshTable();
  };

  const handleEdit = () => {
    MetadataService.getMetadata(accountId, item.client_department_id).then(
      (metadata) => {
        setPopupProps({
          index: i,
          hidden: false,
          isEditMode: true,
          metadata,
        });
      }
    );
  };

  return (
    <TableRow key={i}>
      <ArchiveConfirmationDialog
        open={confirmArchiveOpen}
        callback={handlePromptArchive}
        departmentName={item.data.friendly_name}
        loading={loading}
      />
      <RestoreConfirmationDialog
        open={confirmRestoreOpen}
        callback={handlePromptRestore}
        departmentName={item.data.friendly_name}
        loading={loading}
      />
      {columns.map((column, x) => {
        return (
          <StyledTableCell key={`${i}-${x}`} sx={{ overflow: "initial" }}>
            {column === "status" ? (
              <MetadataStatusChip
                key={`status-${i}=${x}`}
                status={item[column]}
              />
            ) : columnsMap[column].nested ? (
              column === "friendly_name" ? (
                item["data"]["department_name"] || item["data"][column]
              ) : (
                item["data"][column]
              )
            ) : (
              item[column]
            )}
          </StyledTableCell>
        );
      })}
      <StyledTableCell key={`${i}-actions`}>
        <IconButton onClick={openMenu}>
          <MoreVertical />
        </IconButton>
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClick={closeMenu}
          onClose={closeMenu}
          transformOrigin={{ horizontal: "right", vertical: "top" }}
          anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
          PaperProps={{
            sx: {
              width: "175px",
              ".MuiList-root": {
                padding: 0,
              },
              "& .MuiMenuItem-root": {
                height: "40px",
                "&:active": {
                  backgroundColor: (theme) =>
                    alpha(
                      theme.palette.primary.main,
                      theme.palette.action.selectedOpacity
                    ),
                },
                "&:hover": {
                  backgroundColor: (theme) => theme.palette.primary.main,
                  "& .MuiTypography-root": {
                    color: (theme) =>
                      `${theme.palette.primary.contrastText} !important`,
                  },
                },
              },
            },
          }}
        >
          <MenuItem onClick={handleView}>
            <Typography color="textPrimary" variant="body1">
              View
            </Typography>
          </MenuItem>
          {item.status != "ARCHIVED" && (
            <MenuItem onClick={handleEdit}>
              <Typography color="textPrimary" variant="body1">
                Edit {metadataLabel.toLowerCase()}
              </Typography>
            </MenuItem>
          )}
          {item.status === "ARCHIVED" ? (
            <MenuItem onClick={() => setConfirmRestoreOpen(true)}>
              <Typography color="textPrimary" variant="body1">
                Restore {metadataLabel.toLowerCase()}
              </Typography>
            </MenuItem>
          ) : (
            <MenuItem onClick={() => setConfirmArchiveOpen(true)}>
              <Typography color="textPrimary" variant="body1">
                Archive {metadataLabel.toLowerCase()}
              </Typography>
            </MenuItem>
          )}
        </Menu>
      </StyledTableCell>
    </TableRow>
  );
};

const TableRowSkeleton = ({ colSpan }) => {
  return (
    <TableContainer
      sx={{
        borderRadius: "unset !important",
        boxSizing: "border-box",
        borderTop: "none !important",
      }}
    >
      <Table>
        <TableBody>
          <TableRow>
            <StyledTableCell colSpan={colSpan}>
              <Skeleton />
            </StyledTableCell>
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const StyledTableCell = styled(TableCell)(() => ({
  "&.MuiTableCell-root": {
    whiteSpace: "unset",
    verticalAlign: "middle",
  },
}));

export default MetadataTable;
